import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { APP_TITLE } from "../../../config/constant";
import ListComponent from "../../common/lists/ListComponent";
import { useNavigate, useOutletContext } from "react-router";
import { useSelector } from "react-redux";
import { AppQueryClient } from "../../../api/queryClient";
import { defaultStatusColors, getOpenHoursSpan, getSpanWithColor } from "../../../utils/CardUtils";
import { formatDefaultDateTime } from "../../../utils/Formatter";
import MainWindowHeader from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import { useQueryClient } from "react-query";
import * as Library from "../../../utils/Library";

// #region SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: hidden;
        height: 100%;
        margin-right: -23px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }
`;
// #endregion

export default function FullfillmentCentersPage() {
    document.title = `${APP_TITLE} - Fullfillment Centers`;

    // #region VARS
    const { MainLayoutProps, layoutType } = useOutletContext();
    const navigate = useNavigate();
    const navigations = useSelector(state => state.navigationAccesses);
    const queryClient = useQueryClient();
    const [listNumRows, setListNumRows] = useState(50);

    //---------- Header Filters
    const [allRetailersList, setAllRetailersList] = useState([]);
    const [filteredRetailers, setFilteredRetailers] = useState([]);
    //-----
    const [allStoresList, setAllStoresList] = useState([]);
    const [filteredStores, setFilteredStores] = useState([]);
    //-----
    const [requestEnabled, setRequestsEnabled] = useState(false);

    const filters = {
        filterRetailBrandIDs: filteredRetailers?.map(item => item?.value),
        filterStoreIDs: filteredStores?.map(item => item?.value)
    };

    const listQueryKey = ["FFCList", filters];

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setupHeaders(
            props,
            onClickRefreshAction,
            navigations,
            allRetailersList,
            allStoresList,
            onStoresChange,
            onRetailersChange,
            onFiltersLoaded
        );
    }, [
        props?.layoutType,
        navigations,
        allRetailersList,
        allStoresList,
        filteredStores,
        filteredRetailers,
        requestEnabled
    ]);

    useEffect(() => {
        const interval = setInterval(() => {
            onClickRefreshAction();
        }, 180000);

        return () => clearInterval(interval);
    }, []);
    useEffect(() => {
        Library.removeQueries(queryClient, listQueryKey);
    }, []);
    // #endregion

    // #region METHODS
    const onRetailersChange = selection => {
        setFilteredRetailers([selection]);

        const currentRetailBrandID = selection?.value;
        sessionStorage.setItem("currentRetailBrand", currentRetailBrandID || "");
    };

    const onStoresChange = (selection, dropdownDataWithSelections) => {
        setFilteredStores(selection);
        setAllStoresList(dropdownDataWithSelections);
    };

    const onFiltersLoaded = data => {
        const defaultRetailBrand =
            sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");

        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map((item, index) => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }

                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: item?._id === defaultRetailBrand
                };
            });

            const defaultRetailBrandObj =
                retailBrands?.find(item => item?.value === defaultRetailBrand) || retailBrands?.[0];

            setAllRetailersList(retailBrands);
            setFilteredRetailers([defaultRetailBrandObj]);
        }

        if (data?.stores?.length) {
            const stores = data?.stores?.map(item => {
                return {
                    name: item?.name,
                    value: item?._id,
                    selected: true,
                    retailBrandID: item?.retailBrandID
                };
            });

            setAllStoresList(stores);
            setFilteredStores(stores);
        }

        setRequestsEnabled(true);
    };

    const onClickRefreshAction = () => {
        AppQueryClient.invalidateQueries(listQueryKey);
    };
    // #endregion

    // #region LIST COMPONENT PROPS
    const defaultSortSettings = {
        sortable: true,
        sortFunction: (a, b) => a?.name?.toLowerCase()?.localeCompare(b?.name?.toLowerCase())
    };

    const listColumns = [
        {
            name: "Store Name",
            selector: row => row?.storeName ?? "--"
        },
        {
            name: "City, Country",
            selector: row =>
                !row?.city && !row?.country
                    ? "--"
                    : (row?.city || "--") + ", " + (row?.country || row?.countryValue || "--"),
            ...defaultSortSettings,
            width: "12%"
        },
        {
            name: "Affiliated to",
            selector: row => row?.affiliatedTo ?? "--"
        },
        {
            name: "Pickup",
            selector: row => getSpanWithColor(row?.pickup, defaultStatusColors),
            width: "4.5%"
        },
        {
            name: "Courier",
            selector: row => getSpanWithColor(row?.courier, defaultStatusColors),
            width: "4.6%"
        },
        {
            name: "Shipping",
            selector: row => getSpanWithColor(row?.shipping, defaultStatusColors),
            width: "10.6%"
        },
        {
            name: "Open/Closed",
            selector: row =>
                row?.status === "Active" || row?.status === "Fulfilling" ? getOpenHoursSpan(row?.isOpen) : "--",
            width: "9.9%"
        },
        {
            name: "Brands",
            selector: row => row?.numBrands ?? "--"
        },
        {
            name: "Affiliated on",
            selector: row => formatDefaultDateTime(row?.affiliatedOn) ?? "--"
        },
        {
            name: "POS Status",
            selector: row => getSpanWithColor(row?.POSstatus, defaultStatusColors, "text-gray-100")
        },

        {
            name: "Affiliation status",
            selector: row => row?.affiliationStatus ?? "--"
        }
    ];
    // #endregion

    // #region RENDER
    return (
        <StyledPage>
            <ListComponent
                title="Fulfillment Centers"
                columns={listColumns}
                pageLimit={listNumRows}
                currentPage={1}
                queryKey={listQueryKey}
                actions={{
                    includeActions: true,
                    editIsVisible: false,
                    suspendIsVisible: false,
                    removeIsVisible: false,
                    extraActions: [
                        {
                            title: "Details",
                            //onClick: () => navigate("/ffcenters/details"),
                            isDisabled: () => true
                        },
                        {
                            title: "Stock",
                            onClick: row => navigate(`/stock?storeID=${row?.storeID}&storeName=${row?.storeName}`),
                            isDisabled: () => false
                        }
                    ]
                }}
                includeStatus={{
                    width: "100px",
                    stickyStatus: true,
                    statusFromPath: "status",
                    statusColors: defaultStatusColors
                }}
                api={{
                    endpoint: "getFulfillmentCentersData",
                    formData: {
                        companyID: localStorage.getItem("currentCompanyID"),
                        limit: listNumRows,
                        ...filters
                    }
                }}
                requestEnabled={requestEnabled}
                onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
            />
        </StyledPage>
    );
    // #endregion
}

// #region FUNCTIONS
function setupHeaders(
    props,
    onClickRefreshAction,
    navigations,
    allRetailersList,
    allStoresList,
    onStoresChange,
    onRetailersChange,
    onFiltersLoaded
) {
    const createMode = navigations?.navigationAccesses?.fulfillmentCenters?.createModeOnly;

    props.setControlBarLeftBtns([{ to: "#", disabled: true, title: "FFCenters", icon: "fa-magnifying-glass" }]);
    props.setControlBarCenterBtns([
        { to: "/ffcenters/management", active: false, disabled: false, title: "MANAGEMENT" },
        { to: "/ffcenters", active: true, disabled: createMode, title: "FFCENTERS" }

        /*{
            to: "products/stock", active: false, disabled: false, title: "STOCK" 
        }*/
    ]);
    props.setControlBarRightBtns([
        { to: "#", active: true, disabled: createMode, title: "View", icon: "eye" },
        { to: "/labelbrands/request", active: false, disabled: createMode, title: "Request", icon: "mail" },
        { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    gridEnabled: false,
                    listEnabled: true
                }}
                onRefreshClick={onClickRefreshAction}
                firstOrganize={{
                    visible: true,
                    dropdownData: allRetailersList,
                    onDropdownChange: onRetailersChange,
                    disabled: false,
                    type: "singleSelect"
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allStoresList,
                    onDropdownChange: onStoresChange,
                    type: "multiSelect",
                    multidropdownOptions: {
                        labelAllSelected: "All stores",
                        labelNoneSelected: "--",
                        allItems: allStoresList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        useStores: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
            />
        )
    });
}
// #endregion
